import { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { AlertTriangle, Blend, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CircleDashed, Clock, Copy, Globe, Mail, OctagonX, Table, Undo2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleTrigger, AnimatedCollapsibleContent } from "@/components/ui/collapsible"
import { DataTableReact } from "@/components/ui/data-table-react"
import { DATA_TYPE_ITEMS } from "@/components/ui/plate-editor"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetFooter, SheetTitle } from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { RequestPreview } from "@/Pages/Agents/RequestPreview"

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 16 : -16, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -16 : 16, opacity: 0 }),
}

const DATA_TYPE_ICON_MAP = DATA_TYPE_ITEMS.reduce((acc, item) => {
  acc[item.key] = item.icon
  return acc
}, {})

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(isoString) {
  if (!isoString) return "—"
  return new Date(isoString).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit",
  })
}

function formatDuration(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null
  const ms = new Date(completedAt) - new Date(startedAt)
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

// Convert a JSONB headers object {Key: Value} to the [{key, value}] array RequestPreview expects
function headersToArray(headers) {
  if (!headers || typeof headers !== "object" || Array.isArray(headers)) return []
  return Object.entries(headers).map(([key, value]) => ({ key, value: String(value) }))
}

// Build table rows from schema fields + extracted data.
// Supports both the {fields:[{name,value}]} format (same as source_email.extracted_data)
// and a flat {field_name: value} object as fallback.
function buildExtractedRows(schemaFields, extractedData) {
  const normalize = s => s?.toLowerCase().replace(/[\s_]+/g, " ").trim()
  return schemaFields.map((field) => {
    let value = null
    if (extractedData?.fields?.length) {
      const found = extractedData.fields.find(f => normalize(f.name) === normalize(field.name))
      value = found?.value ?? null
    } else if (extractedData && typeof extractedData === "object") {
      value = extractedData[field.name] ?? null
    }
    return { key: field.name, data_type: field.data_type, value }
  })
}

// ── EmailPreview ──────────────────────────────────────────────────────────────

const EMAIL_PREVIEW_COLLAPSED_HEIGHT = 240

function EmailPreview({ body }) {
  const [expanded, setExpanded] = useState(false)
  const wrappedBody = `
    <style>
      html, body { margin: 0; padding: 0; overflow: visible; }
      #email-wrapper {
        padding: 16px;
        display: table;
      }
    </style>
    <div id="email-wrapper">${body}</div>
    <script>
      (function() {
        function measureAndReport() {
          var wrapper = document.getElementById('email-wrapper');
          if (!wrapper) return;
          window.parent.postMessage({
            type: 'emailSize',
            width: wrapper.offsetWidth,
            height: wrapper.offsetHeight
          }, '*');
        }
        measureAndReport();
        document.querySelectorAll('img').forEach(function(img) {
          if (!img.complete) img.onload = measureAndReport;
        });
      })();
    </script>
  `

  const [iframeSize, setIframeSize] = useState(null)
  // Track whether we've already rendered with a measurement so the initial
  // snap to the correct height is instant — only expand/collapse animates.
  const prevIframeSizeRef = useRef(null)

  useEffect(() => {
    function handleMessage(e) {
      if (e.data?.type === 'emailSize') {
        const { width, height } = e.data
        if (width > 0 && height > 0) {
          setIframeSize({
            width: Math.max(width, 200),
            height: Math.max(height, 50),
          })
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Keep the ref in sync after each render so wasLoaded reflects the previous frame.
  useEffect(() => { prevIframeSizeRef.current = iframeSize })

  function handleLoad(e) {
    const iframe = e.target
    try {
      const doc = iframe.contentDocument
      if (doc?.documentElement) doc.documentElement.scrollTop = 0
      if (doc?.body) doc.body.scrollTop = 0
    } catch {
      // Ignore cross-origin errors
    }
  }

  const isExpandable = iframeSize !== null && iframeSize.height > EMAIL_PREVIEW_COLLAPSED_HEIGHT
  const targetHeight = !iframeSize
    ? EMAIL_PREVIEW_COLLAPSED_HEIGHT
    : expanded ? iframeSize.height : Math.min(iframeSize.height, EMAIL_PREVIEW_COLLAPSED_HEIGHT)
  // Animate only when the user expands/collapses, not on the initial measurement snap.
  const wasLoaded = prevIframeSizeRef.current !== null

  return (
    <div className="rounded-md border border-border overflow-hidden relative group/preview">
      <motion.div
        initial={false}
        animate={{ height: targetHeight }}
        transition={{ duration: wasLoaded ? 0.3 : 0, ease: "easeInOut" }}
      >
        <ScrollArea className="w-full bg-white h-full" orientation="vertical">
          <div style={{ width: "100%", height: iframeSize?.height ?? EMAIL_PREVIEW_COLLAPSED_HEIGHT }}>
            <iframe
              srcDoc={wrappedBody}
              sandbox="allow-same-origin allow-scripts"
              scrolling="no"
              title="Email preview"
              className="bg-white border-0"
              style={{ width: "100%", height: "100%" }}
              onLoad={handleLoad}
            />
          </div>
        </ScrollArea>
      </motion.div>

      <AnimatePresence>
        {!expanded && isExpandable && (
          <motion.div
            className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />
        )}
      </AnimatePresence>

      {isExpandable && (
        <div className="absolute inset-x-0 bottom-3 flex justify-center opacity-0 group-hover/preview:opacity-100 transition-opacity duration-200 pointer-events-none">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(v => !v)}
            className="pointer-events-auto h-7 px-2.5 text-xs gap-1 group/btn"
          >
            <ChevronDown className={`size-3 transition-transform duration-300 text-muted-foreground group-hover/btn:text-foreground ${expanded ? "-rotate-180" : ""}`} />
            {expanded ? "Collapse" : "View full email"}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── ResponsePreview ──────────────────────────────────────────────────────────
// Mirrors RequestPreview's visual structure for the response side of a run.

function ResponsePreview({ status, headers = [], body }) {
  const [copied, setCopied] = useState(null)
  const [headersOpen, setHeadersOpen] = useState(true)
  const [bodyOpen, setBodyOpen] = useState(true)

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
    setCopied(type)
    setTimeout(() => setCopied(null), 2000)
  }

  const formattedBody = (() => {
    if (!body) return null
    if (typeof body === "string") {
      try { return JSON.stringify(JSON.parse(body), null, 2) } catch { return body }
    }
    return JSON.stringify(body, null, 2)
  })()

  const statusVariant = status >= 200 && status < 300 ? "success"
    : status >= 400 ? "destructive"
    : "warning"

  return (
    <div className="rounded-lg border border-border bg-muted/50 overflow-hidden">
      {/* Status */}
      <div className="px-3 py-2 border-b border-border bg-background">
        <Badge variant={statusVariant} size="small">{status}</Badge>
      </div>

      {/* Headers */}
      {headers.length > 0 && (
        <Collapsible open={headersOpen} onOpenChange={setHeadersOpen}>
          <CollapsibleTrigger className="group flex items-center justify-between w-full px-3 py-2 border-b border-border bg-background hover:bg-muted transition-colors cursor-pointer">
            <span className="text-[13px] font-medium text-muted-foreground">
              Headers ({headers.length})
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground group-hover:text-foreground transition duration-200 ${headersOpen ? "" : "-rotate-90"}`} />
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent open={headersOpen}>
            <div className="@container/headers">
              <div className="grid @xs/headers:grid-cols-[auto_1fr] text-[13px] font-mono">
                {headers.map(({ key, value }, i) => (
                  <div key={i} className="grid @xs/headers:col-span-2 @xs/headers:grid-cols-subgrid border-t border-border first:border-t-0 last:border-b">
                    <span className="px-3 pt-2 @xs/headers:py-2 text-slate-500 whitespace-nowrap">{key}</span>
                    <span className="px-3 pb-2 @xs/headers:py-2 text-foreground break-all">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedCollapsibleContent>
        </Collapsible>
      )}

      {/* Body */}
      {formattedBody && (
        <Collapsible open={bodyOpen} onOpenChange={setBodyOpen} className="-mb-px">
          <CollapsibleTrigger className="group flex items-center justify-between w-full px-3 py-2 border-b border-border bg-background hover:bg-muted transition-colors cursor-pointer">
            <span className="text-[13px] font-medium text-muted-foreground">Body</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground group-hover:text-foreground transition duration-200 ${bodyOpen ? "" : "-rotate-90"}`} />
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent open={bodyOpen}>
            <div className="relative">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1.5 right-2 h-7 w-7 p-0 z-10 text-muted-foreground hover:text-accent-foreground"
                    onClick={() => copyToClipboard(formattedBody, "body")}
                  >
                    {copied === "body" ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy body</TooltipContent>
              </Tooltip>
              <ScrollArea orientation="both" scrollBarClassName="h-2.5 border-b border-b-transparent">
                <pre className="px-3 py-2 text-[13px] font-mono text-foreground">
                  <code>{formattedBody}</code>
                </pre>
              </ScrollArea>
            </div>
          </AnimatedCollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

// ── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status, size = "small" }) {
  switch (status) {
    case "pending":
    case "extracting":
    case "requesting":
      return (
        <Badge variant="pending" size={size}>
          <CircleDashed className="text-current" />
          {status === "pending" ? "Pending" : status === "extracting" ? "Extracting" : "Requesting"}
        </Badge>
      )
    case "completed":
      return <Badge variant="success" size={size}><CircleCheck className="text-current" />Completed</Badge>
    case "errored":
      return <Badge variant="warning" size={size}><AlertTriangle className="text-current" />Errored</Badge>
    case "failed":
      return <Badge variant="destructive" size={size}><OctagonX className="text-current" />Failed</Badge>
    case "skipped":
      return <Badge variant="outline" size={size}>Skipped</Badge>
    default:
      return <Badge variant="outline" size={size}>{status}</Badge>
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export function RunDetailSheet({ run, open, onOpenChange, onPrev, onNext, direction = 0 }) {
  const scrollViewportRef = useRef(null)

  // Reset scroll position whenever the selected run changes
  useEffect(() => {
    if (scrollViewportRef.current) scrollViewportRef.current.scrollTop = 0
  }, [run?.id])

  if (!run) return null

  const duration = formatDuration(run.started_at, run.completed_at)

  const schemaFields = run.data_schema_snapshot?.fields ?? []
  const extractedRows = schemaFields.length > 0
    ? buildExtractedRows(schemaFields, run.extracted_data)
    : []
  const hasRawExtractedData = run.extracted_data && Object.keys(run.extracted_data).length > 0

  const requestHeaders = headersToArray(run.request_headers)
  const responseHeaders = headersToArray(run.response_headers)

  const emailBody = run.email_body ?? ""

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[580px] sm:max-w-[580px] p-0 flex flex-col gap-0"
        showCloseButton={false}
      >
        {/* ── Sticky header ── */}
        <div className="border-b px-5 pt-4 pb-[15px] shrink-0 relative">
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={run.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1, ease: "easeInOut" }}
              className="flex items-center gap-3"
            >
              <SheetTitle className="text-base font-medium leading-snug line-clamp-2 flex-1">
                {run.email_subject || <span className="text-muted-foreground italic font-normal">No subject</span>}
              </SheetTitle>
              <StatusBadge status={run.status} size="medium" />
            </motion.div>
          </AnimatePresence>

        </div>

        {/* ── Scrollable sections ── */}
        <ScrollArea viewportRef={scrollViewportRef} className="flex-1 min-h-0">
        <div className="relative overflow-hidden [contain:inline-size]">
        <AnimatePresence initial={false} mode="popLayout" custom={direction}>
        <motion.div
          key={run.id}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="w-full"
        >

          {/* Error callout — matches EditSidebar's error style */}
          {run.error_message && (
            <div className="py-4 px-5">
              <div className="flex gap-1.5 text-[13px] text-destructive">
                <OctagonX className="h-3 w-3 shrink-0 mt-0.75" />
                <span>{run.error_message}</span>
              </div>
            </div>
          )}

          {/* Email */}
          <div className="py-4 px-5">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1 h-7">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>Email</span>
            </div>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">From</p>
                  <p className="text-sm break-words">{run.email_from || "—"}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm">{run.email_date ? formatDateTime(run.email_date) : "—"}</p>
                </div>
              </div>
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Subject</p>
                <p className="text-sm break-words">{run.email_subject || "—"}</p>
              </div>
              {emailBody ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Body</p>
                  <EmailPreview body={emailBody} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No email body</p>
              )}
            </div>
          </div>

          {/* Extracted Data — same DataTableReact pattern as EditSidebar */}
          <div className="py-4 px-5">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1 h-7">
              <Table className="h-4 w-4 text-muted-foreground" />
              <span>Data</span>
            </div>
            {extractedRows.length > 0 ? (
              <DataTableReact
                resizable
                sortable
                wrapCells
                minColumnWidth={150}
                maxColumnWidth={400}
                columns={[
                  {
                    accessorKey: "key",
                    header: "Name",
                    cell: (value, row) => {
                      const dataTypeKey = typeof row?.data_type === "string" ? row.data_type.toLowerCase() : "text"
                      const Icon = DATA_TYPE_ICON_MAP[dataTypeKey] || Blend
                      return (
                        <span className="inline-flex gap-1.5">
                          <Icon className="size-3.5 mt-0.75 shrink-0 text-muted-foreground" />
                          {value}
                        </span>
                      )
                    },
                  },
                  { accessorKey: "value", header: "Value" },
                ]}
                data={extractedRows}
              />
            ) : hasRawExtractedData ? (
              <pre className="text-[13px] font-mono bg-muted rounded-md p-3 whitespace-pre-wrap break-all overflow-y-auto max-h-48">
                {JSON.stringify(run.extracted_data, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">No data extracted</p>
            )}
          </div>

          {/* Request — uses RequestPreview directly */}
          <div className="py-4 px-5">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1 h-7">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span>Request</span>
            </div>
            {run.request_url ? (
              <RequestPreview
                method={run.request_method || "POST"}
                url={run.request_url}
                headers={requestHeaders}
                body={run.request_body}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No request made</p>
            )}
          </div>

          {/* Response — ResponsePreview mirrors RequestPreview's structure */}
          <div className="py-4 px-5">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1 h-7">
              <Undo2 className="h-4 w-4 text-muted-foreground" />
              <span>Response</span>
            </div>
            {run.response_status ? (
              <ResponsePreview
                status={run.response_status}
                headers={responseHeaders}
                body={run.response_body}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No response received</p>
            )}
          </div>

          {/* Timing */}
          <div className="py-4 px-5">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1 h-7">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Timing</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm">{formatDateTime(run.created_at)}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="text-sm">{run.started_at ? formatDateTime(run.started_at) : "—"}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-sm">{run.completed_at ? formatDateTime(run.completed_at) : "—"}</p>
              </div>
              {duration && (
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm">{duration}</p>
                </div>
              )}
            </div>
          </div>

        </motion.div>
        </AnimatePresence>
        </div>
        </ScrollArea>

        {/* ── Footer ── */}
        <SheetFooter className="flex-row items-center justify-between border-t px-5 py-3 gap-2">
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={onPrev} disabled={!onPrev} aria-label="Previous run" className="group/prev">
                  <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover/prev:text-foreground transition-colors" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Previous run</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon-sm" onClick={onNext} disabled={!onNext} aria-label="Next run" className="group/next">
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover/next:text-foreground transition-colors" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Next run</TooltipContent>
            </Tooltip>
          </div>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
